import React from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../../layout/Layout';
import LoadingSpinner from './LoadingSpinner';
import { isAppPagePath } from '../../../functions/isAppPagePath';
import classes from './PageLoadingFallback.module.css';

/**
 * Suspense fallback that maintains the Layout structure during lazy loading
 * to prevent the "blink" effect when navigating between pages.
 * 
 * Only shows Layout for authenticated routes (not login pages).
 */
const PageLoadingFallback = () => {
  const location = useLocation();
  
  // Routes that don't use Layout (login pages, public pages, donor pages, parent login pages)
  // Note: Parent pages like /parents/statement, /parents/students, /parents/registration DO use Layout
  const routesWithoutLayout = [
    '/login',
    '/login-app',
    '/portal-user-signup',
    '/donors/login',
    '/donors/signup',
    '/donors/dashboard',
    '/parents/login',
    '/parents/signup',
    '/payments-online',
    '/meal-punch'
  ];
  
  // Routes that DO use Layout (even if they contain parent paths)
  // These take precedence over the routesWithoutLayout patterns
  const routesWithLayout = [
    '/parents/statement',
    '/parents/students',
    '/parents/registration'
  ];
  
  // Check if current route explicitly uses Layout
  const explicitlyUsesLayout = routesWithLayout.some(route => 
    location.pathname.includes(route)
  );
  
  // Check if current route should not have Layout
  // Use includes() to handle dynamic route segments like /:organization/:school/parents/login
  // Embedded -app pages (WorkHub webview) must never show the sidebar, including while lazy routes load.
  const shouldNotShowLayout = (routesWithoutLayout.some(route => 
    location.pathname.includes(route)
  ) && !explicitlyUsesLayout) || isAppPagePath(location.pathname);
  
  const shouldShowLayout = !shouldNotShowLayout;
  
  if (shouldShowLayout) {
    // Show Layout with spinner to maintain sidebar during navigation
    // Use relative positioning for spinner when inside Layout
    return (
      <Layout>
        <div className={classes['spinner-container']}>
          <LoadingSpinner className={classes['spinner-relative']} />
        </div>
      </Layout>
    );
  }
  
  // For login/public pages, just show spinner without Layout
  return <LoadingSpinner />;
};

export default PageLoadingFallback;

